// ===== GRID =====
export const GRID_COLS = 6;
export const GRID_ROWS = 6;
export const CELL_SIZE = 64;
export const CELL_GAP = 4;
export const GRID_PADDING = 16;

// Derived
export const GRID_WIDTH = GRID_COLS * (CELL_SIZE + CELL_GAP) - CELL_GAP;
export const GRID_HEIGHT = GRID_ROWS * (CELL_SIZE + CELL_GAP) - CELL_GAP;

// ===== GAME DIMENSIONS =====
export const GAME_WIDTH = 414;   // iPhone-ish width
export const GAME_HEIGHT = 896;  // iPhone-ish height

// ===== ELEMENT TIERS =====
export enum Tier {
  Fragment = 1,
  Shard = 2,
  Crystal = 3,
  Gem = 4,
  Core = 5,
  Primal = 6,
  Eternal = 7,
}

export const TIER_NAMES: Record<Tier, string> = {
  [Tier.Fragment]: 'Fragment',
  [Tier.Shard]: 'Shard',
  [Tier.Crystal]: 'Crystal',
  [Tier.Gem]: 'Gem',
  [Tier.Core]: 'Core',
  [Tier.Primal]: 'Primal',
  [Tier.Eternal]: 'Eternal',
};

// ===== ELEMENT FAMILIES =====
export enum Family {
  Fire = 'fire',
  Water = 'water',
  Earth = 'earth',
  Air = 'air',
  Lightning = 'lightning',
  Shadow = 'shadow',
  Light = 'light',
  Void = 'void',
  Wild = 'wild',
}

/** The 8 standard element families (excludes Wild). */
export const STANDARD_FAMILIES: Family[] = [
  Family.Fire, Family.Water, Family.Earth, Family.Air,
  Family.Lightning, Family.Shadow, Family.Light, Family.Void,
];

// Colors match VISUAL-IDENTITY.md Section 3.2 & Section 14.1 exactly
export const FAMILY_COLORS: Record<Family, number> = {
  // Family palette is DISTINCT from the UI accent palette (theme.ts). Colors
  // shifted slightly for the v0.5 "Fractured Cyber-Minimalism" pass so they
  // read as element identities, not UI chrome.
  [Family.Fire]:      0xFF5E2A, // Brighter plasma orange
  [Family.Water]:     0x22D3FF, // Shifted from 0x1FA8BE — avoids collision with new 0x00F0FF UI accent
  [Family.Earth]:     0x38D66E, // Bright jade
  [Family.Air]:       0xC8D0DC,
  [Family.Lightning]: 0xFFD600, // Aligned with ACCENT_GOLD brightness
  [Family.Shadow]:    0x9D56FF, // Brightened
  [Family.Light]:     0xF5E6C0, // Warm cream — pure white still banned
  [Family.Void]:      0x6B6EC6, // Brightened deep indigo
  [Family.Wild]:      0xFF7CE5, // Hot fuchsia — more electric
};

export const FAMILY_LABELS: Record<Family, string> = {
  [Family.Fire]:      'Fire',
  [Family.Water]:     'Water',
  [Family.Earth]:     'Earth',
  [Family.Air]:       'Air',
  [Family.Lightning]: 'Lightning',
  [Family.Shadow]:    'Shadow',
  [Family.Light]:     'Light',
  [Family.Void]:      'Void',
  [Family.Wild]:      'Wild',
};

// ===== ENERGY =====
export const MAX_ENERGY = 50;
export const ENERGY_PER_MERGE = 1;
export const ENERGY_REFILL_INTERVAL_MS = 60_000; // 1 per minute
export const ENERGY_AD_REWARD = 10;

// ===== IDLE =====
export const IDLE_BASE_INTERVAL_MS = 30_000; // Base: 1 fragment every 30s per generator
export const IDLE_MAX_QUEUE = 36;             // Max fragments waiting on grid

// ===== DECAY / CORRUPTION =====
export const DECAY_BASE_THRESHOLD = 12;       // Merges before an element corrupts (early game)
export const DECAY_MIN_THRESHOLD = 6;         // Minimum threshold at high scores
export const DECAY_SCALE_SCORE = 5000;        // Score at which threshold reaches minimum
export const CORRUPTED_FAMILY = 'corrupted';  // Pseudo-family for visual purposes
export const DECAY_WARNING_RATIO = 0.75;     // Show warning at 75% of threshold

// ===== SPLINTER =====
export const SPLINTER_MIN_TIER = Tier.Crystal;  // Must be at least Crystal to splinter
export const SPLINTER_FRAGMENT_COUNT = 2;       // Same-family fragments from splinter

// ===== OVERCHARGE (in-run splinter resource) =====
// Splinter is no longer gated by real-world Energy. Instead, players build an
// "Overcharge" meter through gameplay (merges + chain cascades). This decouples
// a survival mechanic from monetization timers (review fix #1).
export const MAX_OVERCHARGE = 100;
export const OVERCHARGE_PER_MERGE = 6;            // Every merge grants 6 charge
export const OVERCHARGE_PER_CASCADE_STEP = 14;    // Each cascade step grants 14 (skill reward)
export const SPLINTER_OVERCHARGE_COST = 50;       // Splinter consumes half a full bar
export const OVERCHARGE_STARTING = 50;            // Players start each run with one charged splinter

// ===== MERGE =====
export const MERGE_REQUIRED = 2;  // How many same elements needed to merge (pair-merge)

// ===== SCORING =====
export const TIER_POINTS: Record<Tier, number> = {
  [Tier.Fragment]: 1,
  [Tier.Shard]: 5,
  [Tier.Crystal]: 25,
  [Tier.Gem]: 100,
  [Tier.Core]: 500,
  [Tier.Primal]: 2500,
  [Tier.Eternal]: 10000,
};

// ===== GENERATOR UNLOCKS =====
// Tier milestone -> family generator unlocked (order matters for progression)
export const GENERATOR_UNLOCK_TIERS: Array<{ tier: Tier; family: Family }> = [
  { tier: Tier.Crystal, family: Family.Water },
  { tier: Tier.Gem,     family: Family.Earth },
  { tier: Tier.Core,    family: Family.Air },
  { tier: Tier.Core,    family: Family.Lightning },
  { tier: Tier.Primal,  family: Family.Shadow },
  { tier: Tier.Primal,  family: Family.Light },
  { tier: Tier.Eternal, family: Family.Void },
];

// ===== SPAWN (Turn-Based) =====
export const WILD_SPAWN_CHANCE = 0.05;       // 5% chance of wild fragment after merge
export const SPAWN_DELAY_MS = 300;           // Delay before spawning after chain completes

// Escalating spawn count — board fills faster as score rises
export const SPAWN_COUNT_THRESHOLDS: Array<{ score: number; count: number }> = [
  { score: 0,    count: 1 },
  { score: 500,  count: 2 },
  { score: 2000, count: 3 },
];

// ===== FAMILY PROGRESSION =====
export const STARTING_FAMILIES: Family[] = [Family.Fire, Family.Water];

export const FAMILY_UNLOCK_TIERS: Array<{ tier: Tier; families: Family[] }> = [
  { tier: Tier.Crystal,  families: [Family.Earth] },
  { tier: Tier.Gem,      families: [Family.Air, Family.Lightning] },
  { tier: Tier.Core,     families: [Family.Shadow] },
  { tier: Tier.Primal,   families: [Family.Light] },
  { tier: Tier.Eternal,  families: [Family.Void] },
];

/**
 * Maximum number of families a single run can include.
 * Caps board chaos and gives the pre-run draft real strategic weight (review fix #2).
 * The math: a 6×6 (36-cell) board cannot support 8 families × 7 tiers (56 distinct
 * elements) without producing unmergeable orphans, so each run draws from a smaller pool.
 */
export const MAX_ACTIVE_FAMILIES_PER_RUN = 4;

// ===== CHAIN / COMBO =====
export const CHAIN_MULTIPLIER_STEP = 0.5;       // Each cascade step adds 0.5x
export const CHAIN_ANIMATION_DELAY_MS = 200;     // Delay between cascade steps

/** Compute score multiplier for a chain step (1-based: 1=initial merge, 2=first cascade, etc.) */
export function chainStepMultiplier(step: number): number {
  return 1.0 + (step - 1) * CHAIN_MULTIPLIER_STEP;
}

// ===== ANIMATION =====
// Durations match VISUAL-IDENTITY.md Section 6.1 easing table
export const MERGE_TWEEN_DURATION = 350;    // Cubic.Out — "snapping together"
export const SPLINTER_TWEEN_DURATION = 400; // Expo.Out — "shattering"
export const SPAWN_TWEEN_DURATION = 250;    // Back.Out — "appearing with energy"
export const DROP_TWEEN_DURATION = 200;     // Quad.Out — "falling into place"
